import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
  Card,
  CardContent,
  FormGroup,
  Chip,
  Alert,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider
} from '@mui/material';
import MatrixQuestion from './MatrixQuestion';
import {
  Visibility as PreviewIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getShuffledOptions, getShuffledImageOptions } from '../utils/shuffleOptions';
import {
  selectionHint,
  selectionProgressMessage,
  multiSelectBlocksProgress,
  isSelectionCapped,
} from '../utils/multiSelectLimit';

/**
 * LivePreview - Real-time preview of survey as respondent would see it
 * Updates automatically when survey structure changes
 */
export default function LivePreview({ survey, questions, pages, pageLogic, currentPageId }) {
  const [previewPageIndex, setPreviewPageIndex] = useState(0);
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [previewHistory, setPreviewHistory] = useState([0]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState('');
  const [attemptedNext, setAttemptedNext] = useState(false);

  // Reset preview when survey structure changes significantly
  useEffect(() => {
    if (pages.length === 0 || !pages[previewPageIndex]) {
      setPreviewPageIndex(0);
      setPreviewHistory([0]);
      setIsCompleted(false);
      setIsDisqualified(false);
    }
  }, [pages.length, previewPageIndex]);

  // Clear answers for questions that changed type or structure
  useEffect(() => {
    const validAnswers = { ...previewAnswers };
    let changed = false;

    Object.keys(validAnswers).forEach(questionId => {
      const question = questions.find(q => q.id === questionId);
      if (!question) {
        // Question was deleted
        delete validAnswers[questionId];
        changed = true;
      } else {
        // Check if answer format matches question type
        const answer = validAnswers[questionId];
        const needsArray = ['multiple', 'multiple-image', 'ranking'].includes(question.type);
        const hasArray = Array.isArray(answer);
        
        if (needsArray !== hasArray) {
          // Type changed from single to multiple or vice versa
          delete validAnswers[questionId];
          changed = true;
        }
      }
    });

    if (changed) {
      setPreviewAnswers(validAnswers);
    }
  }, [questions]);

  // Get current preview page
  const getCurrentPreviewPage = () => {
    if (!pages || pages.length === 0) return null;
    return pages[previewPageIndex] || null;
  };

  // Get questions for current preview page
  const getCurrentPreviewQuestions = () => {
    const page = getCurrentPreviewPage();
    if (!page) return [];
    
    return page.questionIds
      .map(qId => questions.find(q => q.id === qId))
      .filter(Boolean);
  };

  // Apply page logic
  const getNextPreviewPageIndex = () => {
    if (!pageLogic || pageLogic.length === 0) {
      return { nextIndex: previewPageIndex + 1, reason: null };
    }
    
    const currentPage = getCurrentPreviewPage();
    if (!currentPage) return { nextIndex: previewPageIndex + 1, reason: null };
    
    for (const rule of pageLogic) {
      if (!currentPage.questionIds.includes(rule.questionId)) continue;
      
      const answer = previewAnswers[rule.questionId];
      if (!answer) continue;
      
      // Check if condition has answers array with items, otherwise use single answer
      const conditionAnswers = (Array.isArray(rule.condition?.answers) && rule.condition.answers.length > 0)
        ? rule.condition.answers
        : (rule.condition?.answer ? [rule.condition.answer] : []);
      
      if (!conditionAnswers || conditionAnswers.length === 0) continue;
      
      let matches = false;
      if (Array.isArray(answer)) {
        matches = answer.some(ans => conditionAnswers.includes(ans));
      } else {
        matches = conditionAnswers.includes(String(answer));
      }
      
      if (matches) {
        // Check if action is to end survey (targetPageId === "END")
        if (rule.targetPageId === 'END' || rule.action === 'end') {
          return { nextIndex: pages.length, reason: null };
        } else if (rule.action === 'endWithoutPayment') {
          // End without payment (disqualified)
          return { nextIndex: -1, reason: rule.disqualificationReason || null };
        } else if (rule.action === 'goToPage' && rule.targetPageId) {
          const targetIndex = pages.findIndex(p => p.id === rule.targetPageId);
          if (targetIndex >= 0) {
            return { nextIndex: targetIndex, reason: null };
          }
        }
      }
    }
    
    return { nextIndex: previewPageIndex + 1, reason: null };
  };

  // Check if all required questions on current page are answered
  const areRequiredQuestionsAnswered = () => {
    const currentQuestions = getCurrentPreviewQuestions();
    
    for (const question of currentQuestions) {
      if (multiSelectBlocksProgress(question, previewAnswers[question.id])) return false;
      if (!question.required) continue;
      
      // Skip file type questions in preview (files can't be uploaded in preview)
      if (question.type === 'file') continue;
      
      const answer = previewAnswers[question.id];
      
      // Check if answer is empty
      if (answer === undefined || answer === null || answer === '') {
        return false;
      }
      
      // Check if it's just the __custom__ marker without real content
      if (answer === '__custom__') {
        return false;
      }
      
      // Check if array answer is empty or contains only __custom__ marker
      if (Array.isArray(answer)) {
        const realAnswers = answer.filter(a => a !== '__custom__');
        if (realAnswers.length === 0) return false;
      }
      
      // Check if object answer is empty (for matrix)
      if (typeof answer === 'object' && !Array.isArray(answer) && Object.keys(answer).length === 0) {
        return false;
      }
    }
    
    return true;
  };

  // Check if a specific question is required and unanswered
  const isQuestionUnanswered = (question) => {
    if (multiSelectBlocksProgress(question, previewAnswers[question.id])) return true;
    if (!question.required) return false;
    
    // Skip file type questions in preview (files can't be uploaded in preview)
    if (question.type === 'file') return false;
    
    const answer = previewAnswers[question.id];
    
    // Check if answer is empty
    if (answer === undefined || answer === null || answer === '') {
      return true;
    }
    
    // Check if it's just the __custom__ marker without real content
    if (answer === '__custom__') {
      return true;
    }
    
    // Check if array answer is empty or contains only __custom__ marker
    if (Array.isArray(answer)) {
      const realAnswers = answer.filter(a => a !== '__custom__');
      return realAnswers.length === 0;
    }
    
    // Check if object answer is empty (for matrix)
    if (typeof answer === 'object' && !Array.isArray(answer) && Object.keys(answer).length === 0) {
      return true;
    }
    
    return false;
  };

  const handlePreviewNext = () => {
    // Mark that user attempted to navigate
    setAttemptedNext(true);
    
    // Check required questions
    if (!areRequiredQuestionsAnswered()) {
      return; // Don't proceed if required questions are not answered
    }
    
    const { nextIndex, reason } = getNextPreviewPageIndex();
    
    // Check for disqualification (endWithoutPayment)
    if (nextIndex === -1) {
      setIsCompleted(true);
      setIsDisqualified(true);
      setDisqualificationReason(reason || '');
      return;
    }
    
    if (nextIndex >= pages.length) {
      setIsCompleted(true);
      setIsDisqualified(false);
      setDisqualificationReason('');
      return;
    }
    
    // Reset attemptedNext when successfully navigating
    setAttemptedNext(false);
    setPreviewHistory([...previewHistory, nextIndex]);
    setPreviewPageIndex(nextIndex);
  };

  const handleRestartPreview = () => {
    setPreviewPageIndex(0);
    setPreviewAnswers({});
    setPreviewHistory([0]);
    setIsCompleted(false);
    setIsDisqualified(false);
    setDisqualificationReason('');
    setAttemptedNext(false);
  };

  const handlePreviewBack = () => {
    if (previewHistory.length <= 1) return;
    
    const newHistory = [...previewHistory];
    newHistory.pop();
    const prevIndex = newHistory[newHistory.length - 1];
    
    setPreviewHistory(newHistory);
    setPreviewPageIndex(prevIndex);
    setAttemptedNext(false);
  };

  const handlePreviewAnswerChange = (questionId, value) => {
    setPreviewAnswers({
      ...previewAnswers,
      [questionId]: value
    });
    // Reset validation error when user starts answering
    if (attemptedNext) {
      setAttemptedNext(false);
    }
  };

  const renderPreviewQuestion = (question) => {
    const answer = previewAnswers[question.id];
    const SERVER_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

    // Безопасная проверка существования вопроса
    if (!question) {
      return (
        <Alert severity="warning" variant="outlined">
          <Typography variant="caption">Вопрос не найден</Typography>
        </Alert>
      );
    }

    switch (question.type) {
      case 'single':
        if (!question.options || question.options.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты ответов</Typography>
            </Alert>
          );
        }
        
        const customSingleKey = '__custom__';
        const isCustomSingle = answer !== undefined && answer !== null && !question.options.includes(answer);
        const displayValueSingle = isCustomSingle ? customSingleKey : (answer || '');
        
        return (
          <Box>
            <RadioGroup
              value={displayValueSingle}
              onChange={(e) => {
                if (e.target.value === customSingleKey) {
                  handlePreviewAnswerChange(question.id, customSingleKey);
                } else {
                  handlePreviewAnswerChange(question.id, e.target.value);
                }
              }}
              size="small"
            >
              {getShuffledOptions(question, 'preview').map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  value={option}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">{option}</Typography>}
                />
              ))}
              {question.allowCustomOption && (
                <FormControlLabel
                  value={customSingleKey}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Свой вариант</Typography>}
                />
              )}
            </RadioGroup>
            {question.allowCustomOption && displayValueSingle === customSingleKey && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={answer === customSingleKey ? '' : (answer || '')}
                onChange={(e) => handlePreviewAnswerChange(question.id, e.target.value || customSingleKey)}
                sx={{ mt: 1, pl: 4, boxSizing: 'border-box' }}
              />
            )}
          </Box>
        );

      case 'dropdown':
        if (!question.options || question.options.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты ответов</Typography>
            </Alert>
          );
        }
        
        const customDropdownKey = '__custom__';
        const isCustomDropdown = answer !== undefined && answer !== null && !question.options.includes(answer);
        const displayValueDropdown = isCustomDropdown ? customDropdownKey : (answer || '');
        
        return (
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Выберите вариант</InputLabel>
              <Select
                value={displayValueDropdown}
                label="Выберите вариант"
                onChange={(e) => {
                  if (e.target.value === customDropdownKey) {
                    handlePreviewAnswerChange(question.id, customDropdownKey);
                  } else {
                    handlePreviewAnswerChange(question.id, e.target.value);
                  }
                }}
              >
                {getShuffledOptions(question, 'preview').map((option, idx) => (
                  <MenuItem key={idx} value={option}>
                    {option}
                  </MenuItem>
                ))}
                {question.allowCustomOption && (
                  <MenuItem value={customDropdownKey}>Свой вариант</MenuItem>
                )}
              </Select>
            </FormControl>
            {question.allowCustomOption && displayValueDropdown === customDropdownKey && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={answer === customDropdownKey ? '' : (answer || '')}
                onChange={(e) => handlePreviewAnswerChange(question.id, e.target.value || customDropdownKey)}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'multiple':
        if (!question.options || question.options.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты ответов</Typography>
            </Alert>
          );
        }
        
        const answerArray = Array.isArray(answer) ? answer : [];
        const customValues = answerArray.filter(a => !question.options.includes(a));
        const hasCustom = customValues.length > 0;
        const atSelectionCap = isSelectionCapped(question, answerArray);
        
        return (
          <Box>
            <FormGroup>
              {getShuffledOptions(question, 'preview').map((option, idx) => {
                const optionChecked = Array.isArray(answer) && answer.includes(option);
                return (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      size="small"
                      checked={optionChecked}
                      disabled={!optionChecked && atSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArray)) return;
                        const currentAnswers = Array.isArray(answer) ? answer : [];
                        const newAnswers = e.target.checked
                          ? [...currentAnswers.filter(a => question.options.includes(a) || a === customValues[0]), option]
                          : currentAnswers.filter(a => a !== option);
                        handlePreviewAnswerChange(question.id, newAnswers);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{option}</Typography>}
                />
                );
              })}
              {question.allowCustomOption && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={hasCustom}
                      disabled={!hasCustom && atSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArray)) return;
                        if (e.target.checked) {
                          // Add custom marker
                          const validOptions = answerArray.filter(a => question.options.includes(a));
                          handlePreviewAnswerChange(question.id, [...validOptions, '__custom__']);
                        } else {
                          // Remove all custom values
                          const newAnswer = answerArray.filter(a => question.options.includes(a));
                          handlePreviewAnswerChange(question.id, newAnswer);
                        }
                      }}
                    />
                  }
                  label={<Typography variant="body2">Свой вариант</Typography>}
                />
              )}
            </FormGroup>
            {question.allowCustomOption && hasCustom && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValues.filter(v => v !== '__custom__').join(', ')}
                onChange={(e) => {
                  const validOptions = answerArray.filter(a => question.options.includes(a));
                  if (e.target.value.trim()) {
                    handlePreviewAnswerChange(question.id, [...validOptions, e.target.value]);
                  } else {
                    handlePreviewAnswerChange(question.id, [...validOptions, '__custom__']);
                  }
                }}
                sx={{ mt: 1, pl: 4, boxSizing: 'border-box' }}
              />
            )}
          </Box>
        );

      case 'text':
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            value={answer || ''}
            onChange={(e) => handlePreviewAnswerChange(question.id, e.target.value)}
            placeholder="Текстовый ответ"
          />
        );

      case 'likert':
        const likertOptions = question.likertOptions?.length > 0
          ? question.likertOptions
          : ['Не согласен', 'Нейтрально', 'Согласен'];
        
        return (
          <RadioGroup
            value={answer || ''}
            onChange={(e) => handlePreviewAnswerChange(question.id, e.target.value)}
          >
            {likertOptions.map((option, idx) => (
              <FormControlLabel
                key={idx}
                value={option}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{option}</Typography>}
              />
            ))}
          </RadioGroup>
        );

      case 'single-image':
        const imageOptionsSingle = question.imageOptions || [];
        
        if (imageOptionsSingle.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты с изображениями</Typography>
            </Alert>
          );
        }
        
        const customSingleImgKey = '__custom__';
        const isCustomSingleImg = answer !== undefined && answer !== null && !imageOptionsSingle.find(opt => opt.text === answer);
        const displayValueSingleImg = isCustomSingleImg ? customSingleImgKey : (answer || '');
        
        return (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mb: 2 }}>
              {getShuffledImageOptions(question, 'preview').map((option, idx) => {
                if (!option || !option.text) return null;
                const isSelected = answer === option.text;
                return (
                  <Paper
                    key={idx}
                    elevation={isSelected ? 4 : 1}
                    onClick={() => handlePreviewAnswerChange(question.id, option.text)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        borderColor: 'primary.light',
                        elevation: 3
                      }
                    }}
                  >
                    {option.imageUrl && (
                      <Box
                        component="img"
                        src={option.imageUrl.startsWith('/') ? `${SERVER_BASE}${option.imageUrl}` : option.imageUrl}
                        alt={option.text}
                        sx={{ 
                          width: '100%', 
                          maxHeight: 200, 
                          objectFit: 'contain', 
                          borderRadius: 1, 
                          mb: 1 
                        }}
                      />
                    )}
                    <Typography variant="body2" align="center">
                      {option.text}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
            {question.allowCustomOption && (
              <FormControlLabel
                control={
                  <Radio
                    size="small"
                    checked={displayValueSingleImg === customSingleImgKey}
                    onChange={() => {
                      handlePreviewAnswerChange(question.id, customSingleImgKey);
                    }}
                  />
                }
                label={<Typography variant="body2">Свой вариант</Typography>}
              />
            )}
            {question.allowCustomOption && displayValueSingleImg === customSingleImgKey && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={answer === customSingleImgKey ? '' : (answer || '')}
                onChange={(e) => handlePreviewAnswerChange(question.id, e.target.value || customSingleImgKey)}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'multiple-image':
        const imageOptionsMultiple = question.imageOptions || [];
        
        if (imageOptionsMultiple.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты с изображениями</Typography>
            </Alert>
          );
        }
        
        const answerArrayImg = Array.isArray(answer) ? answer : [];
        const customValuesImg = answerArrayImg.filter(a => !imageOptionsMultiple.find(opt => opt.text === a));
        const hasCustomImg = customValuesImg.length > 0;
        const atImageSelectionCap = isSelectionCapped(question, answerArrayImg);
        
        return (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mb: 2 }}>
              {getShuffledImageOptions(question, 'preview').map((option, idx) => {
                if (!option || !option.text) return null;
                const isSelected = Array.isArray(answer) && answer.includes(option.text);
                const imageBlocked = !isSelected && atImageSelectionCap;
                return (
                  <Paper
                    key={idx}
                    elevation={isSelected ? 4 : 1}
                    onClick={() => {
                      if (imageBlocked) return;
                      const currentAnswers = Array.isArray(answer) ? answer : [];
                      const validAnswers = currentAnswers.filter(a => imageOptionsMultiple.find(opt => opt.text === a) || a === customValuesImg[0]);
                      const newAnswers = isSelected
                        ? validAnswers.filter(a => a !== option.text)
                        : [...validAnswers, option.text];
                      handlePreviewAnswerChange(question.id, newAnswers);
                    }}
                    sx={{
                      p: 1.5,
                      cursor: imageBlocked ? 'not-allowed' : 'pointer',
                      opacity: imageBlocked ? 0.55 : 1,
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        borderColor: imageBlocked ? 'transparent' : 'primary.light',
                        elevation: 3
                      }
                    }}
                  >
                    {option.imageUrl && (
                      <Box
                        component="img"
                        src={option.imageUrl.startsWith('/') ? `${SERVER_BASE}${option.imageUrl}` : option.imageUrl}
                        alt={option.text}
                        sx={{ 
                          width: '100%', 
                          maxHeight: 200, 
                          objectFit: 'contain', 
                          borderRadius: 1, 
                          mb: 1 
                        }}
                      />
                    )}
                    <Typography variant="body2" align="center">
                      {option.text}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
            {question.allowCustomOption && (
              <FormControlLabel
                control={
                    <Checkbox
                      size="small"
                      checked={hasCustomImg}
                      disabled={!hasCustomImg && atImageSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArrayImg)) return;
                        if (e.target.checked) {
                          // Add custom marker
                          const validOptions = answerArrayImg.filter(a => imageOptionsMultiple.find(opt => opt.text === a));
                          handlePreviewAnswerChange(question.id, [...validOptions, '__custom__']);
                        } else {
                        // Remove all custom values
                        const newAnswer = answerArrayImg.filter(a => imageOptionsMultiple.find(opt => opt.text === a));
                        handlePreviewAnswerChange(question.id, newAnswer);
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Свой вариант</Typography>}
              />
            )}
            {question.allowCustomOption && hasCustomImg && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValuesImg.filter(v => v !== '__custom__').join(', ')}
                onChange={(e) => {
                  const validAnswers = answerArrayImg.filter(a => imageOptionsMultiple.find(opt => opt.text === a));
                  if (e.target.value.trim()) {
                    handlePreviewAnswerChange(question.id, [...validAnswers, e.target.value]);
                  } else {
                    handlePreviewAnswerChange(question.id, [...validAnswers, '__custom__']);
                  }
                }}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'ranking':
        if (!question.options || question.options.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты для ранжирования</Typography>
            </Alert>
          );
        }
        
        const rankingAnswers = Array.isArray(answer) ? answer : [];
        const availableOptions = (question.options || []).filter(opt => !rankingAnswers.includes(opt));
        
        return (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Расставьте варианты в порядке предпочтения (нажмите чтобы добавить):
            </Typography>
            
            {/* Selected items in order */}
            {rankingAnswers.length > 0 && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                {rankingAnswers.map((option, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip label={idx + 1} size="small" color="primary" sx={{ minWidth: 32 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{option}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newRanking = [...rankingAnswers];
                        newRanking.splice(idx, 1);
                        handlePreviewAnswerChange(question.id, newRanking);
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Available options to add */}
            {availableOptions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Доступные варианты:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {availableOptions.map((opt, idx) => (
                    <Button
                      key={idx}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        handlePreviewAnswerChange(question.id, [...rankingAnswers, opt]);
                      }}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      {opt}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
            
            {rankingAnswers.length === 0 && availableOptions.length === 0 && (
              <Alert severity="info" variant="outlined">
                <Typography variant="caption">Нет вариантов для ранжирования</Typography>
              </Alert>
            )}
          </Box>
        );

      case 'matrix':
        if (!question.matrixRows || question.matrixRows.length === 0 || 
            !(question.matrixColumns || question.matrixCols) || 
            ((question.matrixColumns || question.matrixCols) || []).length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Настройте строки и колонки матрицы</Typography>
            </Alert>
          );
        }
        
        const matrixAnswers = answer || {};
        const matrixColumns = question.matrixColumns || question.matrixCols || [];
        
        return (
          <MatrixQuestion
            rows={question.matrixRows || []}
            columns={matrixColumns}
            value={matrixAnswers}
            onChange={(next) => handlePreviewAnswerChange(question.id, next)}
          />
        );

      case 'nps':
        const npsValue = answer !== undefined ? answer : null;
        return (
          <Box>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <Button
                  key={num}
                  size="small"
                  variant={npsValue === num ? 'contained' : 'outlined'}
                  onClick={() => handlePreviewAnswerChange(question.id, num)}
                  sx={{ minWidth: 35, p: 0.5 }}
                >
                  {num}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Не рекомендую
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Рекомендую
              </Typography>
            </Box>
          </Box>
        );

      case 'scale':
        const scaleMin = question.scaleMin || 1;
        const scaleMax = question.scaleMax || 10;
        const scaleValue = answer !== undefined ? answer : scaleMin;
        
        return (
          <Box sx={{ px: 1 }}>
            <Slider
              value={scaleValue}
              onChange={(e, val) => handlePreviewAnswerChange(question.id, val)}
              min={scaleMin}
              max={scaleMax}
              step={1}
              marks
              valueLabelDisplay="on"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {question.scaleMinLabel || scaleMin}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {question.scaleMaxLabel || scaleMax}
              </Typography>
            </Box>
          </Box>
        );

      case 'file':
        return (
          <Box>
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ mb: 1 }}
            >
              Выбрать файл
              <input type="file" hidden disabled />
            </Button>
            <Typography variant="caption" color="text.secondary" display="block">
              Максимальный размер файла: 15МБ
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              (Загрузка файлов недоступна в предпросмотре)
            </Typography>
          </Box>
        );

      case 'rating':
        const ratingValue = answer || 0;
        const maxRating = question.ratingMax || 5;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[...Array(maxRating)].map((_, idx) => (
              <IconButton
                key={idx}
                size="small"
                onClick={() => handlePreviewAnswerChange(question.id, idx + 1)}
                sx={{ color: idx < ratingValue ? 'warning.main' : 'action.disabled' }}
              >
                ★
              </IconButton>
            ))}
          </Box>
        );

      default:
        return (
          <Alert severity="info" variant="outlined">
            <Typography variant="caption">
              Предпросмотр для типа "{question.type}" в разработке
            </Typography>
          </Alert>
        );
    }
  };

  // No pages created yet
  if (!pages || pages.length === 0) {
    return (
      <Paper sx={{ p: 2, minHeight: 400, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PreviewIcon color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Тестовое прохождение
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          Создайте страницы и добавьте вопросы для предпросмотра
        </Alert>
      </Paper>
    );
  }

  const previewQuestions = getCurrentPreviewQuestions();
  const progress = ((previewPageIndex + 1) / pages.length) * 100;
  const currentPage = getCurrentPreviewPage();

  return (
    <Paper sx={{ 
      p: { xs: 1.5, sm: 2 },
      height: { xs: 'auto', md: '80vh' },
      minHeight: { xs: 320, md: 0 },
      maxHeight: { xs: 'none', md: 800 },
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'grey.50',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Тестовое прохождение
          </Typography>
        </Box>
        <Chip label="Предпросмотр" size="small" color="primary" variant="outlined" />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Completion Screen */}
      {isCompleted ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          {isDisqualified ? (
            <>
              <Typography variant="h6" gutterBottom color="warning.main">
                ⚠ Завершено без оплаты
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                {disqualificationReason || 'Извините, исходя из Вашего ответа Вы не подходите для этого опроса.'}<br />
                Пожалуйста, продолжайте проходить другие опросы на платформе!<br />
                <br />
                <em>(Это тестовый просмотр. Ответы не сохранены.)</em>
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom color="primary">
                ✓ Опрос завершён
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Тестовое прохождение завершено.<br />
                Ответы не сохранены.
              </Typography>
            </>
          )}
          <Button
            variant="contained"
            onClick={handleRestartPreview}
            startIcon={<PreviewIcon />}
          >
            Пройти заново
          </Button>
        </Box>
      ) : (
        <>
          {/* Survey Content - Scrollable */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            mb: 2,
            minHeight: 0,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}>
            {/* Survey Title */}
            {survey?.title && (
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                {survey.title}
              </Typography>
            )}

            {/* Progress */}
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
            </Box>

            {/* Questions */}
            {previewQuestions.length === 0 ? (
              <Alert severity="info" variant="outlined">
                Нет вопросов на текущей странице.<br />
                Добавьте вопросы на страницу "{currentPage?.title || `Страница ${previewPageIndex + 1}`}"
              </Alert>
            ) : (
              previewQuestions.map((question) => {
                const rangeMessage = selectionProgressMessage(question, previewAnswers[question.id]);
                const hint = selectionHint(question);
                const showError = attemptedNext && isQuestionUnanswered(question);
                return (
                  <Card 
                    key={question.id} 
                    variant="outlined" 
                    sx={{ 
                      mb: 1.5, 
                      bgcolor: 'white',
                      border: showError ? 2 : 1,
                      borderColor: showError ? 'error.main' : 'divider'
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        {question.text}
                        {question.required && (
                          <Typography component="span" color="error" fontSize="small"> *</Typography>
                        )}
                      </Typography>
                      {hint && (
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
                          {hint}
                        </Typography>
                      )}
                      {showError && (
                        <Alert severity="error" sx={{ mt: 1, mb: 1, py: 0, bgcolor: '#ffebee', borderColor: '#f44336' }}>
                          <Typography variant="caption" sx={{ color: '#d32f2f' }} fontWeight="medium">
                            {rangeMessage || 'На этот вопрос обязательно нужно ответить'}
                          </Typography>
                        </Alert>
                      )}
                      <Box sx={{ mt: 1 }}>
                        {renderPreviewQuestion(question)}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })
            )}

            {/* Logic hint */}
            {pageLogic && pageLogic.length > 0 && currentPage && (
              <Alert severity="info" variant="outlined" sx={{ mt: 2, fontSize: '0.75rem' }}>
                <Typography variant="caption">
                  💡 Логика переходов активна: ответы влияют на следующую страницу
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Button
              size="small"
              onClick={handlePreviewBack}
              disabled={previewHistory.length <= 1}
              startIcon={<BackIcon />}
            >
              Назад
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handlePreviewNext}
              endIcon={<NextIcon />}
              disabled={pages.length === 0}
            >
              {previewPageIndex >= pages.length - 1 ? 'Завершить' : 'Далее'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
